/* Root + Spring / Horizon compatibility for the verified SHIPAID 0.3.223 contract.
 * Theme-only instance compatibility. Protection writes reconcile existing fee
 * lines against the runtime's whole-cart pricing; merchandise remains runtime-owned.
 */
(() => {
  if (window.RootSpringProductCompatibility) return;
  const patched = new WeakSet();
  const snapshots = new WeakMap();
  const errors = new WeakMap();
  let protectionQueue = Promise.resolve();
  const storeHost = /^(?:www\.)?rootandspring\.com$|^root-and-spring\.myshopify\.com$/;
  const productPage = () => /\/products\/[^/]+/.test(location.pathname);
  let observer;
  let active = false;

  const labels = {
    title: 'Shipping Protection',
    protected: 'Protected',
    unprotected: 'Unprotected',
    productProtected: 'Add to cart — protected',
    productUnprotected: 'Add to cart — unprotected',
    checkoutProtected: 'Checkout — protected',
    checkoutUnprotected: 'Checkout — unprotected',
    withProtection: ', with Shipping Protection',
    withoutProtection: ', without Shipping Protection',
    benefit: 'Shipping Protection against loss, theft, or damage'
  };
  const plain = text => text.replace(/\s+/g, ' ').trim();
  const setAttribute = (element, name, value) => {
    if (element.getAttribute(name) !== value) element.setAttribute(name, value);
  };
  const describe = (button, label) => {
    if (!button) return;
    setAttribute(button, 'aria-label', label);
    setAttribute(button, 'title', label);
  };

  function showError(widget, message = 'We could not update Shipping Protection. Please try again.') {
    errors.set(widget, message);
    renderError(widget);
  }
  function renderError(widget) {
    const root = widget.shadowRoot;
    if (!root) return;
    let alert = root.querySelector('[data-horizon-protection-error]');
    const message = errors.get(widget);
    if (!message) { alert?.remove(); return; }
    if (!alert) {
      alert = document.createElement('p');
      alert.setAttribute('data-horizon-protection-error', '');
      alert.setAttribute('role', 'alert');
      root.append(alert);
    }
    alert.textContent = message;
  }
  const protectionLines = cart => cart.items.filter(item => item.handle === 'shipaid-protection' && Number(item.quantity) > 0);
  function validCart(cart) {
    if (!Array.isArray(cart?.items) || !Number.isFinite(Number(cart.total_price))) {
      throw new Error('Unable to verify Shopify cart.');
    }
    return cart;
  }
  async function cartRequest(updates) {
    const root = window.Shopify?.routes?.root || '/';
    const response = await fetch(`${root}${updates ? 'cart/update.js' : 'cart.js'}`, {
      credentials: 'same-origin',
      ...(updates ? {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-ShipAid': '1' },
        body: JSON.stringify({ updates })
      } : { headers: { Accept: 'application/json' }, cache: 'no-store' })
    });
    if (!response.ok) throw new Error('Shopify protection update failed.');
    return validCart(await response.json());
  }
  function pricingProvider(owner) {
    const candidates = [owner, ...document.querySelectorAll('shipaid-widget-v2, shipaid-widget')];
    const provider = candidates.find(element => element?._store?.protectionSettings &&
      element?._protectionProduct?.variants?.length && typeof element.calculateProtectionTotal === 'function' &&
      (typeof element.getProtectionVariantForFee === 'function' || typeof element._findProtectionVariant === 'function'));
    if (!provider) throw new Error('Shipping Protection pricing is not ready.');
    return provider;
  }
  async function correctVariant(owner, cart) {
    const provider = pricingProvider(owner);
    const fee = await provider.calculateProtectionTotal(cart);
    if (!Number.isFinite(fee) || fee < 0) throw new Error('Invalid protection fee.');
    if (fee === 0) return null;
    const variant = typeof provider.getProtectionVariantForFee === 'function'
      ? provider.getProtectionVariantForFee(fee) : provider._findProtectionVariant(fee);
    if (!variant?.id || !/^\d+$/.test(String(variant.id))) throw new Error('Missing protection variant.');
    return variant;
  }
  function rememberCart(owner, cart, variant) {
    if (owner.localName === 'shipaid-widget-v2') {
      owner._cart = cart;
      owner._hasProtectionInCart = protectionLines(cart).length > 0;
      if (variant !== undefined) owner._protectionVariant = variant || undefined;
    }
    return cart;
  }
  function reconcileProtection(owner, { remove = false, reader = cartRequest } = {}) {
    const task = protectionQueue.then(async () => {
      // Read after acquiring the queue. Never apply another caller's stale cart.
      let cart = validCart(await reader());
      for (let attempt = 0; attempt < 3; attempt++) {
        const lines = protectionLines(cart);
        if (!lines.length) return rememberCart(owner, cart);
        const variant = remove ? null : await correctVariant(owner, cart);
        const retained = variant && lines.find(item => String(item.variant_id) === String(variant.id));
        if (variant && lines.length === 1 && retained && Number(retained.quantity) === 1) {
          return rememberCart(owner, cart, variant);
        }
        const updates = Object.create(null);
        for (const item of lines) {
          if (!item.key) throw new Error('Protection line has no Shopify key.');
          updates[item.key] = item === retained ? 1 : 0;
        }
        // Shopify supports adding a missing variant in the same update. All
        // existing lines use keys, including split lines for the same variant.
        if (variant && !retained) updates[String(variant.id)] = 1;
        cart = await cartRequest(updates);
        // Re-evaluate the returned cart: a native merchandise change may have
        // completed between our GET and update. Never write its quantities.
      }
      const lines = protectionLines(cart);
      const variant = lines.length && !remove ? await correctVariant(owner, cart) : null;
      if ((!variant && !lines.length) || (variant && lines.length === 1 &&
          String(lines[0].variant_id) === String(variant.id) && Number(lines[0].quantity) === 1)) {
        return rememberCart(owner, cart, variant);
      }
      throw new Error('Protection changed while verifying the cart.');
    });
    protectionQueue = task.catch(() => {});
    return task;
  }

  function patchCheckoutProtection(widget) {
    if (typeof widget.ensureProtectionVariantForCheckout !== 'function' ||
        !widget.ensureProtectionVariantForCheckout.toString().includes('this._cart.items.find')) return;
    widget.ensureProtectionVariantForCheckout = async function() {
      try {
        const cart = await reconcileProtection(this);
        errors.delete(this);
        renderError(this);
        return cart;
      } catch (error) { showError(this); throw error; }
    };
    widget.removeProtection = async function() {
      try {
        const cart = await reconcileProtection(this, { remove: true });
        errors.delete(this);
        renderError(this);
        // Runtime notifications run after the protection queue is released.
        await this.updateCart(cart, { forceFetch: false });
      } catch (error) { showError(this); throw error; }
    };
    if (typeof widget.handleCheckoutClick === 'function' &&
        widget.handleCheckoutClick.toString().includes('this.ensureProtectionVariantForCheckout')) {
      widget.handleCheckoutClick = async function() {
        if (this._toggleLoading) return;
        this._toggleLoading = true;
        try {
          await this.ensureProtectionVariantForCheckout();
          this.proceedToCheckout();
        } catch (error) { showError(this); }
        finally { this._toggleLoading = false; }
      };
    }
    if (typeof widget.handleLegacyPrimaryClick === 'function' &&
        widget.handleLegacyPrimaryClick.toString().includes('this.addProtection')) {
      widget.handleLegacyPrimaryClick = async function() {
        if (this._toggleLoading) return;
        this._toggleLoading = true;
        try {
          await this.ensureProtectionVariantForCheckout();
          if (!this._hasProtectionInCart) await this.addProtection();
          await this.ensureProtectionVariantForCheckout();
          this._enterToggleMode('checked');
          this.proceedToCheckout();
        } catch (error) { showError(this); }
        finally { this._toggleLoading = false; }
      };
    }
    widget.requestUpdate();
  }

  function patchProtectedAdd(widget) {
    if (typeof widget.handlePrimaryClick !== 'function' || typeof widget.fetchCart !== 'function' ||
        !widget.handlePrimaryClick.toString().includes('const cart = await this.fetchCart()')) return;
    const originalClick = widget.handlePrimaryClick;
    const originalFetch = widget.fetchCart;
    let action = false;
    widget.fetchCart = async function() {
      if (!action) return originalFetch.call(this);
      try {
        return await reconcileProtection(this, { reader: () => originalFetch.call(this) });
      } catch (error) {
        showError(this, 'Your item was added, but Shipping Protection could not be updated. Review your cart before checkout.');
        throw error;
      }
    };
    widget.handlePrimaryClick = async function(event) {
      event.preventDefault();
      if (action || this.primaryLoading) return;
      action = true;
      errors.delete(this);
      renderError(this);
      try {
        pricingProvider(this);
        await originalClick.call(this, event);
      } catch (error) { showError(this); }
      finally { action = false; }
    };
    widget.requestUpdate();
  }

  function snapshot(widget) {
    const button = widget.addToCartButton;
    if (!(button instanceof HTMLButtonElement)) return null;
    let saved = snapshots.get(button);
    if (saved && button.dataset.horizonShipaidChoice === 'unprotected' &&
        button.querySelector('.add-to-cart-text')?.textContent === labels.productUnprotected) {
      return saved;
    }
    // Horizon can morph the contents without replacing the button itself.
    // Our own last write is tracked, so it never becomes the next original.
    if (!saved || button.innerHTML !== saved.lastHTML) {
      const html = !saved && widget.originalAtcLabel ? widget.originalAtcLabel : button.innerHTML;
      const template = document.createElement('template');
      template.innerHTML = html;
      const label = template.content.querySelector('.add-to-cart-text') || template.content;
      const copy = label.cloneNode(true);
      copy.querySelectorAll('svg, [aria-hidden="true"], .add-to-cart__added, .loading__spinner, .visually-hidden')
        .forEach(node => node.remove());
      saved = { html, label: plain(copy.textContent || ''), lastHTML: button.innerHTML };
      snapshots.set(button, saved);
      widget.originalAtcLabel = html;
    }
    return saved;
  }

  // Change text nodes only: replacing textContent on Lit-rendered containers
  // would destroy the comment anchors used by its next render.
  function firstText(element, value) {
    if (!element) return;
    const node = Array.from(element.childNodes).find(child => child.nodeType === Node.TEXT_NODE && child.data.trim());
    if (node && node.data !== value) node.data = value;
  }

  function decorate(widget, isProduct) {
    renderError(widget);
    const root = widget.shadowRoot;
    if (!root || (widget.lang && !/^en(?:-|$)/i.test(widget.lang))) return;
    const protectedChoice = isProduct || widget._uiMode !== 'toggle' || widget._desiredProtection;
    firstText(root.querySelector('.cp-title'), labels.title + ' ');
    firstText(root.querySelector('.cp-title small'), protectedChoice ? labels.protected : labels.unprotected);
    const icon = root.querySelector('.cp-icon');
    if (icon) setAttribute(icon, 'alt', '');
    firstText(root.querySelector('.cp-benefits li'), labels.benefit);
    const primary = root.querySelector('.cp-primary');
    if (primary) {
      if (!(isProduct && widget.primaryLoading)) {
        firstText(primary, isProduct ? labels.productProtected :
          protectedChoice ? labels.checkoutProtected : labels.checkoutUnprotected);
      }
      describe(primary, plain(primary.textContent || '') +
        (protectedChoice ? labels.withProtection : labels.withoutProtection));
    }
    const secondary = root.querySelector('.cp-secondary');
    if (secondary) {
      firstText(secondary, labels.checkoutUnprotected);
      describe(secondary, plain(secondary.textContent || '') + labels.withoutProtection);
    }
    const checkbox = root.querySelector('.cp-checkbox input[type="checkbox"]');
    if (checkbox) describe(checkbox, labels.title);
  }

  function wrapUpdated(widget, isProduct) {
    const originalUpdated = widget.updated;
    widget.updated = function(changed) {
      originalUpdated.call(this, changed);
      decorate(this, isProduct);
    };
  }

  function patch(widget) {
    if (!(widget instanceof HTMLElement)) return false;
    if (patched.has(widget)) return true;
    if (widget.localName === 'shipaid-widget-v2') {
      if (typeof widget._renderLegacyContent !== 'function' || typeof widget._renderToggleContent !== 'function' ||
          !widget._renderToggleContent.toString().includes('this._desiredProtection') || typeof widget.updated !== 'function') return false;
      patched.add(widget);
      patchCheckoutProtection(widget);
      wrapUpdated(widget, false);
      decorate(widget, false);
      return true;
    }
    if (widget.localName !== 'shipaid-add-cart-plus' ||
        typeof widget.updateVisibility !== 'function' || !widget.updateVisibility.toString().includes('hasTogglePreference') ||
        typeof widget.updateOriginalButtonLabel !== 'function' || !widget.updateOriginalButtonLabel.toString().includes('span.innerHTML = label') ||
        typeof widget.refreshOffer !== 'function' || typeof widget.updated !== 'function') return false;

    patched.add(widget);
    patchProtectedAdd(widget);
    const originalRefreshOffer = widget.refreshOffer;
    let pendingOffers = 0;
    widget.captureOriginalButtonLabel = function() { snapshot(this); };
    widget.restoreOriginalButtonLabel = function() {
      const saved = snapshot(this);
      if (!saved) return;
      const button = this.addToCartButton;
      if (button.innerHTML !== saved.html) button.innerHTML = saved.html;
      saved.lastHTML = button.innerHTML;
      button.removeAttribute('data-horizon-shipaid-choice');
      describe(button, saved.label);
    };
    widget.updateOriginalButtonLabel = function() {
      const saved = snapshot(this);
      if (!saved) return;
      const button = this.addToCartButton;
      // Repair a pre-existing 0.3.223 nested restore before applying the label.
      if (button.querySelectorAll('.add-to-cart-text').length !== 1) button.innerHTML = saved.html;
      const label = button.querySelector('.add-to-cart-text');
      if (!label) return;
      if (label.textContent !== labels.productUnprotected) label.textContent = labels.productUnprotected;
      saved.lastHTML = button.innerHTML;
      setAttribute(button, 'data-horizon-shipaid-choice', 'unprotected');
      describe(button, labels.productUnprotected + labels.withoutProtection);
      this.tagOriginalButton();
    };
    widget.updateVisibility = function() {
      const button = this.addToCartButton;
      const ready = pendingOffers === 0 && this.storeData?.planActive === true &&
        this.storeData?.addToCartPlus?.enabled === true && this.selectedVariantAvailable === true &&
        !this.primaryDisabled && Boolean(this.protectionVariantId) && this.priceLabel != null &&
        button instanceof HTMLButtonElement && (!button.disabled || this.primaryLoading);
      this.isWidgetVisible = ready;
      this.styleOriginalATC = ready;
      if (!ready) {
        this.restoreOriginalButtonLabel();
        this.tagOriginalButton();
        return;
      }
      // A cart checkbox preference does not remove the next product's choices.
      // The cached value itself is neither read nor changed by this adapter.
      this.primaryLabel = labels.productProtected;
      this.updateOriginalButtonLabel();
      this.ensureHostPlacement();
    };
    widget.refreshOffer = async function(...args) {
      pendingOffers++;
      this.updateVisibility();
      try {
        return await originalRefreshOffer.apply(this, args);
      } finally {
        pendingOffers--;
        this.updateVisibility();
      }
    };
    wrapUpdated(widget, true);
    snapshot(widget);
    widget.updateVisibility();
    decorate(widget, true);
    return true;
  }

  function scan(records = []) {
    if (!active) return;
    document.querySelectorAll('shipaid-widget-v2').forEach(patch);
    if (productPage()) document.querySelectorAll('shipaid-add-cart-plus').forEach(widget => {
      if (!patch(widget)) return;
      if (records.some(record => record.type === 'attributes' && record.target === widget.addToCartButton)) {
        widget.updateVisibility();
      }
    });
  }
  function stop() {
    active = false;
    observer?.disconnect();
    observer = undefined;
  }
  function start() {
    if (active || !storeHost.test(location.hostname)) return;
    active = true;
    scan();
    observer = new MutationObserver(scan);
    observer.observe(document.documentElement, {
      childList: true, subtree: true, attributes: true, attributeFilter: ['disabled']
    });
    for (const name of ['shipaid-add-cart-plus', 'shipaid-widget-v2']) {
      customElements.whenDefined(name).then(() => scan());
    }
  }

  window.RootSpringProductCompatibility = { start, stop, patch };
  if (storeHost.test(location.hostname)) {
    window.addEventListener('pagehide', stop);
    window.addEventListener('pageshow', start);
    start();
  }
})();
